// The comment thread under a plain feed post.
//
// ⚠ PLAIN POSTS ONLY. `postCard()` does not call this for an event card, and
// the server refuses all three routes for one anyway -- belt and braces on
// purpose, because the rule is a permission decision rather than a layout one.
// Event chat is RSVP-gated and a group can disable it; comments are gated by
// membership alone, so a composer on an announcement card would let a member
// refused from poker chat reach exactly the same people. Decided with Will
// 2026-09-10; see docs/plan-post-comments.md.
//
// ⚠ COLLAPSED BY DEFAULT, and the thread is fetched only when opened. A feed
// page renders twenty cards; loading every thread up front is twenty requests
// for conversations nobody has asked to read yet. The COUNT comes down with
// the post, so the button can say how many there are without fetching any.

import { api, ApiClientError } from '../api/client.js';
import { el } from '../dom.js';
import { fmtRelative } from '@msc/shared';
import type { FeedPost, PostComment } from '@msc/shared';

/**
 * The "N comments" control and, once opened, the thread plus a composer.
 *
 * Returns one element the card can append. It owns its own open/closed state
 * and its own fetch, so the feed does not have to track either.
 */
export function commentThread(post: FeedPost, zone: string): HTMLElement {
  const wrap = el('div', { class: 'comments' });
  const list = el('div', { class: 'comment-list' });
  const error = el('p', { class: 'form-error', hidden: true });

  let open = false;
  let loaded = false;
  let comments: PostComment[] = [];
  // The count starts from the post payload and is kept in step locally
  // afterwards, so the button is right before the first fetch and after every
  // write without re-reading the whole feed.
  let count = post.commentCount;

  const toggle = el('button', { type: 'button', class: 'btn btn-link btn-sm comment-toggle' });

  const label = (): string => {
    if (count === 0) return open ? 'Hide' : 'Comment';
    return `${open ? 'Hide' : 'Show'} ${count} comment${count === 1 ? '' : 's'}`;
  };
  const paintToggle = (): void => {
    toggle.textContent = label();
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  /**
   * Add a comment we do not already have, from EITHER direction.
   *
   * ⚠ BOTH PATHS MUST DEDUPE, and the reason is a race that only appears with
   * realtime running: the server broadcasts the frame before the HTTP response
   * gets back to the poster, so `__acceptComment` can append the comment first
   * and then `onAdded` appends the very same row again. The database had one
   * row and the screen showed two. Caught by counting rendered `.comment`
   * nodes against the table, not by reading the code -- the poster is the one
   * person who sees it, and only for as long as they stay on the page.
   */
  function upsert(c: PostComment): void {
    if (comments.some((x) => x.commentId === c.commentId)) return;
    comments = [...comments, c];
    count = comments.length;
    paintList();
    paintToggle();
  }

  function paintList(): void {
    list.replaceChildren(
      ...comments.map((c) => commentRow(c, zone, () => remove(c))),
      composer(post.postId, upsert, error),
    );
  }

  async function remove(c: PostComment): Promise<void> {
    error.hidden = true;
    try {
      await api(`/comments/${c.commentId}`, { method: 'DELETE' });
      comments = comments.filter((x) => x.commentId !== c.commentId);
      count = comments.length;
      paintList();
      paintToggle();
    } catch (err) {
      error.textContent =
        err instanceof ApiClientError ? err.message : 'Could not delete that.';
      error.hidden = false;
    }
  }

  toggle.addEventListener('click', async () => {
    open = !open;
    list.hidden = !open;
    paintToggle();
    if (!open || loaded) return;

    list.replaceChildren(el('p', { class: 'muted small' }, 'Loading…'));
    try {
      comments = await api<PostComment[]>(`/posts/${post.postId}/comments`);
      loaded = true;
      // ⚠ Trust the SERVER's count over the one that arrived on the post. The
      // post's number was computed when the page loaded and the thread may
      // have moved since -- and the block filter applies to both, so they can
      // legitimately differ from what somebody else sees.
      count = comments.length;
      paintList();
      paintToggle();
    } catch (err) {
      list.replaceChildren(
        el(
          'p',
          { class: 'form-error' },
          err instanceof ApiClientError ? err.message : 'Could not load the comments.',
        ),
      );
    }
  });

  list.hidden = true;
  paintToggle();
  wrap.replaceChildren(toggle, error, list);

  // Let the feed's realtime handler push a comment in without re-rendering the
  // whole card. Attached to the element so the caller needs no registry.
  (wrap as CommentHost).__acceptComment = (c: PostComment): void => {
    if (c.postId !== post.postId) return;
    if (comments.some((x) => x.commentId === c.commentId)) return;
    // While the thread is CLOSED the array is empty and must stay that way --
    // opening it fetches the real list. Only the visible count moves, or the
    // first open would render one comment above a fetch that returns all of
    // them.
    if (!loaded) {
      count += 1;
      paintToggle();
      return;
    }
    upsert(c);
  };
  (wrap as CommentHost).__removeComment = (commentId: number): void => {
    const before = comments.length;
    comments = comments.filter((x) => x.commentId !== commentId);
    if (comments.length !== before) count = comments.length;
    else if (count > 0) count -= 1;
    if (loaded) paintList();
    paintToggle();
  };

  return wrap;
}

/** The two hooks the feed's realtime dispatcher calls. */
export interface CommentHost extends HTMLElement {
  __acceptComment?: (c: PostComment) => void;
  __removeComment?: (commentId: number) => void;
}

function commentRow(c: PostComment, zone: string, onDelete: () => void): HTMLElement {
  const children: HTMLElement[] = [
    el('div', { class: 'comment-head' }, [
      el('a', { class: 'comment-who', href: `/users/${c.userId}` }, c.displayName),
      el('span', { class: 'muted small' }, fmtRelative(c.createdAt, zone, Date.now())),
    ]),
    // textContent, so a comment is text and never markup. The server stores it
    // raw on purpose -- escaping belongs at the point of render, once.
    el('p', { class: 'comment-body' }, c.body),
  ];
  if (c.canDelete) {
    const del = el('button', { type: 'button', class: 'btn btn-link btn-sm btn-danger' }, 'Delete');
    del.addEventListener('click', onDelete);
    children.push(del);
  }
  return el('div', { class: 'comment', 'data-author': c.userId }, children);
}

function composer(
  postId: number,
  onAdded: (c: PostComment) => void,
  error: HTMLElement,
): HTMLElement {
  const box = el('textarea', {
    rows: '2',
    maxlength: '2000',
    placeholder: 'Write a comment…',
    'aria-label': 'Write a comment',
  }) as HTMLTextAreaElement;
  const send = el('button', { type: 'submit', class: 'btn btn-sm' }, 'Post');

  // ⚠ A real <form> with method="post". JavaScript submits it, but if the
  // script ever fails the browser must not fall back to GET -- that is a hard
  // rule in CLAUDE.md, and it costs nothing to honour here.
  const form = el('form', { method: 'post', class: 'comment-form' }, [box, send]) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const body = box.value.trim();
    if (!body) return;
    error.hidden = true;
    send.setAttribute('disabled', 'disabled');
    try {
      const added = await api<PostComment>(`/posts/${postId}/comments`, {
        method: 'POST',
        body: { body },
      });
      box.value = '';
      onAdded(added);
    } catch (err) {
      error.textContent =
        err instanceof ApiClientError ? err.message : 'Could not post that.';
      error.hidden = false;
    } finally {
      send.removeAttribute('disabled');
    }
  });

  // Enter sends, Shift+Enter makes a new line -- the convention every chat box
  // uses, and a comment is almost always one line.
  box.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      form.requestSubmit();
    }
  });

  return form;
}
